import { Injectable, inject, OnDestroy } from '@angular/core';
import { onAuthStateChanged, User, Unsubscribe } from 'firebase/auth';
import { Observable, BehaviorSubject, map } from 'rxjs';
import { FirebaseService } from './firebase.service';
import {
  UserClaims,
  ResourceType,
  ActionType,
  UserRole,
  Permission,
  PermissionChecker,
  DEFAULT_ROLE_TEMPLATES
} from '../types/permission.types';

@Injectable({
  providedIn: 'root'
})
export class PermissionService implements PermissionChecker, OnDestroy {
  private firebaseService = inject(FirebaseService);
  private currentUserClaims$ = new BehaviorSubject<UserClaims | null>(null);
  private currentUser$ = new BehaviorSubject<User | null>(null);
  private unsubscribe: Unsubscribe | null = null;

  constructor() {
    // Listen for auth state changes
    this.unsubscribe = onAuthStateChanged(this.firebaseService.auth, async (user: User | null) => {
      console.log('PermissionService - Auth state changed:', user ? 'user logged in' : 'user logged out');
      this.currentUser$.next(user);

      if (user) {
        try {
          // Get fresh token with claims
          const idTokenResult = await user.getIdTokenResult(true);

          // Handle both old and new claim formats
          const rawClaims = idTokenResult.claims;

          // Always create a UserClaims object for authenticated users
          // Default to 'front_desk' for users without a role (guest users shouldn't access portal)
          const userRole = (rawClaims['role'] as UserRole) || (rawClaims['super'] ? 'super_admin' : 'front_desk');

          // Get permissions: use explicit permissions from claims, or fall back to role template
          let permissions: Permission[] = rawClaims['permissions'] as Permission[] || [];
          if (permissions.length === 0 && userRole) {
            // No explicit permissions - use role template as fallback
            const roleTemplate = DEFAULT_ROLE_TEMPLATES.find(t => t.role === userRole);
            if (roleTemplate) {
              permissions = roleTemplate.permissions;
            }
          }

          const processedClaims: UserClaims = {
            role: userRole,
            permissions: permissions,
            clientId: rawClaims['clientId'] as string | undefined,
            metadata: rawClaims['metadata'] as UserClaims['metadata']
          };

          this.currentUserClaims$.next(processedClaims);
        } catch (error) {
          console.error('PermissionService - Error getting claims:', error);
          this.currentUserClaims$.next(null);
        }
      } else {
        this.currentUserClaims$.next(null);
      }
    });
  }

  // Observables for reactive components
  get userClaims$(): Observable<UserClaims | null> {
    return this.currentUserClaims$.asObservable();
  }

  get user$(): Observable<User | null> {
    return this.currentUser$.asObservable();
  }

  get isAuthenticated$(): Observable<boolean> {
    return this.currentUser$.pipe(map(user => !!user));
  }

  get isSuperAdmin$(): Observable<boolean> {
    return this.currentUserClaims$.pipe(
      map(claims => claims?.role === 'super_admin')
    );
  }

  get isClientAdmin$(): Observable<boolean> {
    return this.currentUserClaims$.pipe(
      map(claims => claims?.role === 'client_admin')
    );
  }

  get currentUserRole$(): Observable<UserRole | null> {
    return this.currentUserClaims$.pipe(map(claims => claims?.role || null));
  }

  get currentClientId$(): Observable<string | null> {
    return this.currentUserClaims$.pipe(map(claims => claims?.clientId || null));
  }

  // Core Permission Checking
  hasPermission(
    resource: ResourceType,
    action: ActionType,
    scope?: {
      clientId?: string;
      propertyId?: string;
      locationId?: string;
      campaignId?: string;
    }
  ): boolean {
    const claims = this.currentUserClaims$.value;
    if (!claims) {
      return false;
    }

    // Super admin has all permissions
    if (claims.role === 'super_admin') {
      return true;
    }

    // Check if user has the required permission
    const hasResourcePermission = claims.permissions?.some(permission => {
      if (permission.resource !== resource) return false;
      if (!permission.actions.includes(action)) return false;

      // Check scope if provided
      if (scope) {
        if (scope.clientId && permission.scope?.clientIds) {
          return permission.scope.clientIds.includes(scope.clientId);
        }
        if (scope.propertyId && permission.scope?.propertyIds) {
          return permission.scope.propertyIds.includes(scope.propertyId);
        }
        if (scope.locationId && permission.scope?.locationIds) {
          return permission.scope.locationIds.includes(scope.locationId);
        }
        if (scope.campaignId && permission.scope?.campaignIds) {
          return permission.scope.campaignIds.includes(scope.campaignId);
        }
      }

      return true;
    });

    return hasResourcePermission || false;
  }

  canAccessClient(clientId: string): boolean {
    const claims = this.currentUserClaims$.value;
    if (!claims) return false;

    // Super admin can access all clients
    if (claims.role === 'super_admin') {
      return true;
    }

    // User can access their assigned client
    if (claims.clientId === clientId) {
      return true;
    }

    // Check if user has explicit permission for this client
    return claims.permissions?.some(permission =>
      permission.scope?.clientIds?.includes(clientId)
    ) || false;
  }

  canAccessProperty(propertyId: string): boolean {
    const claims = this.currentUserClaims$.value;
    if (!claims) return false;

    // Super admin can access all properties
    if (claims.role === 'super_admin') {
      return true;
    }

    // Check if user has explicit permission for this property
    return claims.permissions?.some(permission =>
      permission.scope?.propertyIds?.includes(propertyId)
    ) || false;
  }

  canAccessLocation(locationId: string): boolean {
    const claims = this.currentUserClaims$.value;
    if (!claims) return false;

    // Super admin can access all locations
    if (claims.role === 'super_admin') {
      return true;
    }

    // Check if user has explicit permission for this location
    return claims.permissions?.some(permission =>
      permission.scope?.locationIds?.includes(locationId)
    ) || false;
  }

  canAccessCampaign(campaignId: string): boolean {
    const claims = this.currentUserClaims$.value;
    if (!claims) return false;

    // Super admin can access all campaigns
    if (claims.role === 'super_admin') {
      return true;
    }

    // Check if user has campaign permissions
    return this.hasPermission('campaigns', 'read', { campaignId });
  }

  getAccessibleClients(): string[] {
    const claims = this.currentUserClaims$.value;
    if (!claims) return [];

    // Super admin has access to all clients (return empty array to indicate "all")
    if (claims.role === 'super_admin') {
      return [];
    }

    const clientIds = new Set<string>();

    // Add user's assigned client
    if (claims.clientId) {
      clientIds.add(claims.clientId);
    }

    // Add clients from explicit permissions
    claims.permissions?.forEach(permission => {
      permission.scope?.clientIds?.forEach(id => clientIds.add(id));
    });

    return Array.from(clientIds);
  }

  getAccessibleProperties(): string[] {
    const claims = this.currentUserClaims$.value;
    if (!claims) return [];

    // Super admin has access to all properties
    if (claims.role === 'super_admin') {
      return [];
    }

    const propertyIds = new Set<string>();

    claims.permissions?.forEach(permission => {
      permission.scope?.propertyIds?.forEach(id => propertyIds.add(id));
    });

    return Array.from(propertyIds);
  }

  getAccessibleCampaigns(): string[] {
    const claims = this.currentUserClaims$.value;
    if (!claims) return [];

    // Super admin has access to all campaigns
    if (claims.role === 'super_admin') {
      return [];
    }

    const campaignIds = new Set<string>();

    claims.permissions?.forEach(permission => {
      if (permission.resource === 'campaigns') {
        permission.scope?.campaignIds?.forEach(id => campaignIds.add(id));
      }
    });

    return Array.from(campaignIds);
  }

  // Synchronous methods for PermissionChecker interface
  userRole(): UserRole | null {
    const claims = this.currentUserClaims$.value;
    return claims?.role || null;
  }

  userClaims(): UserClaims | null {
    return this.currentUserClaims$.value;
  }

  // Cleanup method
  ngOnDestroy(): void {
    if (this.unsubscribe) {
      this.unsubscribe();
    }
  }

  // Role Management Utilities
  getRoleTemplate(role: UserRole) {
    return DEFAULT_ROLE_TEMPLATES.find(template => template.role === role);
  }

  getAllRoleTemplates() {
    return DEFAULT_ROLE_TEMPLATES;
  }

  // Permission Helpers for Common UI Scenarios
  canManageUsers(): boolean {
    return this.hasPermission('users', 'manage') || this.hasPermission('users', 'create');
  }

  canManageCampaigns(): boolean {
    return this.hasPermission('campaigns', 'manage') || this.hasPermission('campaigns', 'create');
  }

  canViewAnalytics(): boolean {
    return this.hasPermission('analytics', 'read');
  }

  canManageProperties(): boolean {
    return this.hasPermission('properties', 'manage') || this.hasPermission('properties', 'create');
  }

  canRedeemPrizes(): boolean {
    return this.hasPermission('prizes', 'update');
  }

  // Client Switching (for super admins)
  canSwitchClients(): boolean {
    const claims = this.currentUserClaims$.value;
    return claims?.role === 'super_admin';
  }

  // Refresh user claims
  async refreshClaims(): Promise<void> {
    const user = this.currentUser$.value;
    if (user) {
      const idTokenResult = await user.getIdTokenResult(true);
      const claims = idTokenResult.claims as unknown as UserClaims;
      this.currentUserClaims$.next(claims);
    }
  }

  // Get current claims synchronously
  getCurrentClaims(): UserClaims | null {
    return this.currentUserClaims$.value;
  }

  // Get current user synchronously
  getCurrentUser(): User | null {
    return this.currentUser$.value;
  }
}
